/**
 * EA Report Preview Component (New Structure)
 *
 * This component displays the EA Report preview page with the same layout and format
 * as the original ProjectEAReportPreview, but uses the refactored calculation functions
 * from eaReportCalculations.ts to support the new data structure (contractRatePeriods/environmentalRatePeriods).
 *
 * It reuses all UI components from EAReport folder (SummarySection, BestOfferSection, etc.)
 * to maintain visual consistency while using the new calculation logic.
 */

import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import PrintIcon from '@mui/icons-material/Print';
import { AppBar, Box, Fab, Toolbar, Typography } from '@mui/material';
import { useLocalStorageState } from 'ahooks';
import { filter, groupBy, isEmpty, map, pick, uniq } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { ReactToPrint } from 'react-to-print';
import { useShareLink } from '~/api/share';
import { session } from '~/core/session/hooks';
import { MailComposer } from '~/features/mail/MailComposer';
import { ProjectOffer } from '~/models/project';
import { User } from '~/models/user';
import reportCover from '~static/bg-solar.png';
import logoBlueSrc from '~static/logo-blue.png';
import { EAReportData } from '../EAReport/types';
import { getEATemplate } from '../emailTemplates';
import BestOfferSection from './BestOfferSection';
import ContractTermComparisonSection from './ContractTermComparisonSection';
import CurrentVsNewBillSection from './CurrentVsNewSection';
import { aggregateForDisplay, calculateOffersForMeters, formatMeters } from './eaReportCalculations';
import { EAReportGroupedMeter } from './reportTypes';
import SitesSection from './SitesSection';
import { ReportContainer, Section, SectionContainer, Title } from './style';
import SummarySection from './SummarySection';

/** Filter offers to only include periods whose id is in displayOfferPeriodIds. Removes offers with no periods. */
function filterOffersByDisplayPeriodIds<T extends { periods?: { id?: string }[] }>(
  offers: T[],
  displayOfferPeriodIds: string[]
): T[] {
  if (!displayOfferPeriodIds?.length) return offers;
  return filter(
    map(offers, (offer) => ({
      ...offer,
      periods: filter(offer.periods || [], (p) => p.id != null && displayOfferPeriodIds.includes(p.id))
    })),
    (offer) => (offer.periods?.length ?? 0) > 0
  ) as T[];
}

/**
 * ProjectEAReportPreview Component (New Structure)
 *
 * This component:
 * 1. Reads EAReportData from localStorage or share link
 * 2. Uses refactored calculation functions (formatMeters, calculateOffersForMeters)
 *    that support the new contractRatePeriods/environmentalRatePeriods structure
 * 3. Renders the same UI components as the original preview for consistency
 */
const ProjectEAReportPreview: React.FC = () => {
  const { pathname, search } = useLocation();
  const { id } = useParams();
  const componentRef = useRef<HTMLDivElement>(null);
  const [mailComposerOpened, setMailComposerOpened] = useState(false);

  let data: EAReportData | undefined;
  if (pathname.includes('share/') && id) {
    const { data: shareLink } = useShareLink(id, search.split('pwd=')[1]);
    data = (shareLink?.metadata ?? {}) as unknown as EAReportData;
  } else {
    const [projectEAReportData] = useLocalStorageState<EAReportData>('projectEAReportData');
    data = projectEAReportData;
  }

  useEffect(() => {
    if (!pathname.includes('share/')) {
      session.updateUserInfo();
    }
  }, []);

  if (isEmpty(data)) {
    return null;
  }

  const {
    projectId,
    reportType,
    company,
    companyName,
    contactUser,
    meterGroups,
    meters,
    offers: rawOffers,
    greenPriceLookUps,
    companyContacts,
    isPrintVersion = false
  } = data;

  /** Show segment (?) help on BEST OFFERS and CONTRACT TERM COMPARISON only in preview; hide in print and share. */
  const showSegmentHelp = !isPrintVersion && !pathname.includes('share/');

  // Normalize offers so share link / stored data always has contractRatePeriods and environmentalRatePeriods
  // (ProjectOffer.from converts legacy "periods" into contractRatePeriods/environmentalRatePeriods)
  const offers = (rawOffers || []).map((o) => ProjectOffer.from(o));

  // Use refactored calculation functions from eaReportCalculations.ts
  // These functions support the new contractRatePeriods/environmentalRatePeriods structure
  // Simplified logic similar to old EAReport: directly use recommendedPeriodId to mark isBest
  //
  // IMPORTANT:
  // 1. Split groups (different meter-offer combinations) should remain separate groups
  // 2. But for BEST OFFERS, we need to show all retailers that have offers for the meters in each group
  // 3. So we need to find all offers that contain any of the meters from related split groups

  // Group split groups by base name to find related groups
  const splitGroupsMap = new Map<string, typeof meterGroups>();

  meterGroups.forEach((group) => {
    if (group.name.includes(' - ')) {
      const baseName = group.name.split(' - ')[0];
      if (!splitGroupsMap.has(baseName)) {
        splitGroupsMap.set(baseName, []);
      }
      const groups = splitGroupsMap.get(baseName);
      if (groups) {
        groups.push(group);
      }
    }
  });

  // Process each group separately (don't merge split groups)
  // Each split group represents a different meter-offer combination and should be displayed separately
  // For BEST OFFERS and CONTRACT TERM COMPARISON, we group by retailer and calculate based on set meters
  const processedGroups = meterGroups.map((group) => {
    // Filter meters for this group
    const groupMeters = meters.filter((meter) => group.meterIds.includes(meter.id));

    // If no meters, return empty result
    if (groupMeters.length === 0) {
      return {
        meters: [],
        offers: [],
        recommendedPeriodId: group.recommendedPeriodId || '',
        name: group.name,
        currentContestableCost: 0,
        currentAvgCostPerKwh: 0,
        lwaDLF: 1,
        lwaMLF: 1,
        annualData: {} as any,
        weighedAverageData: {} as any
      } as unknown as EAReportGroupedMeter;
    }

    const formattedMeters = formatMeters(groupMeters.map((v) => v as any));

    // Use offers from group.offerIds (from form) - no need to require offer contains all meters
    // Filter offers by group.offerIds
    const filteredOffersForComparison = offers.filter((offer) => offer.id && group.offerIds.includes(offer.id));

    // Additional deduplication by offer id (in case same offer appears multiple times)
    const uniqueOffersMap = new Map<string, ProjectOffer>();
    filteredOffersForComparison.forEach((offer) => {
      if (offer.id && !uniqueOffersMap.has(offer.id)) {
        uniqueOffersMap.set(offer.id, offer);
      }
    });
    const deduplicatedOffers = Array.from(uniqueOffersMap.values());

    const recommendedPeriodId = group.recommendedPeriodId || '';
    const displayOfferPeriodIds = group.displayOfferPeriodIds || [];

    // If no offers or no display periods, return empty result
    if (deduplicatedOffers.length === 0 || displayOfferPeriodIds.length === 0) {
      return {
        ...formattedMeters,
        meters: formattedMeters.meters,
        offers: [],
        recommendedPeriodId: group.recommendedPeriodId || '',
        name: group.name
      } as unknown as EAReportGroupedMeter;
    }

    // Use individual offers: pass FULL contractRatePeriods for calculation so 24M includes 12M segment
    // (displayOfferPeriodIds filter only applies to output via filterOffersByDisplayPeriodIds)
    const projectRawOffers = deduplicatedOffers
      .map((offer) => {
        const contractRatePeriods = offer.contractRatePeriods || [];
        if (contractRatePeriods.length === 0) return null;
        // Must have at least one period in displayOfferPeriodIds for this offer to be relevant
        const hasDisplayPeriod = contractRatePeriods.some(
          (p: { id?: string }) => p.id && displayOfferPeriodIds.includes(p.id)
        );
        if (!hasDisplayPeriod) return null;
        return {
          ...offer,
          meterIds: group.meterIds || [],
          isBest: false,
          contractRatePeriods,
          environmentalRatePeriods: offer.environmentalRatePeriods || []
        };
      })
      .filter(Boolean) as any[];

    const calculatedOffers = calculateOffersForMeters({
      ...pick(formattedMeters, ['currentAvgCostPerKwh', 'lwaDLF', 'lwaMLF', 'annualData', 'weighedAverageData']),
      projectMeters: formattedMeters.meters as any[],
      projectRawOffers,
      greenPriceLookUps: greenPriceLookUps || []
    });

    // Aggregate for display: one row per (retailer, periodName), same layout as before
    const displayOffers = filterOffersByDisplayPeriodIds(
      aggregateForDisplay(calculatedOffers, recommendedPeriodId, formattedMeters.meters as any[]),
      displayOfferPeriodIds
    );

    return {
      ...formattedMeters,
      meters: formattedMeters.meters,
      offers: displayOffers as any,
      recommendedPeriodId: group.recommendedPeriodId || '',
      name: group.name
    } as unknown as EAReportGroupedMeter;
  });

  // Group by offer set name (extract set name from group name)
  // If group name is "GroupName - SetName", extract "SetName"
  // Otherwise, use the group name directly as set name
  const groupsBySetName = groupBy(processedGroups, (group) => {
    const groupName = group.name;
    // Check if name contains " - " (format: "GroupName - SetName")
    if (groupName.includes(' - ')) {
      // Extract set name (part after " - ")
      return groupName.split(' - ').slice(1).join(' - ');
    }
    // Otherwise, use the full name as set name
    return groupName;
  });

  // Process each set name with unified logic (single group = array of one, multi group = merge)
  const groupedMeters: EAReportGroupedMeter[] = Object.entries(groupsBySetName).map(([setName, groups]) => {
    // 1. Merge meters (deduplicate by meter id); works for both single and multiple groups
    const mergedMeterIds = uniq(groups.flatMap((group) => group.meters.map((m) => m.id)));
    const mergedMeters = mergedMeterIds
      .map((meterId) => groups.flatMap((group) => group.meters).find((m) => m.id === meterId))
      .filter(Boolean) as any[];

    // 2. Build meterOfferPeriodMapping: each meter keeps its original (processed) group's offer/period mapping
    const meterOfferPeriodMapping: Record<
      string,
      { offerIds: string[]; displayOfferPeriodIds: string[]; recommendedPeriodId: string }
    > = {};
    mergedMeterIds.forEach((meterId) => {
      // Find the original meter group that contains this meter (by meterIds), so each meter keeps its own recommendedPeriodId
      const originalGroup = meterGroups.find((g) => g.meterIds?.includes(meterId));
      if (originalGroup) {
        meterOfferPeriodMapping[meterId] = {
          offerIds: originalGroup.offerIds || [],
          displayOfferPeriodIds: originalGroup.displayOfferPeriodIds || [],
          recommendedPeriodId: originalGroup.recommendedPeriodId || ''
        };
      }
    });

    const mergedOfferIds = uniq(Object.values(meterOfferPeriodMapping).flatMap((m) => m.offerIds));
    const mergedDisplayOfferPeriodIds = uniq(
      Object.values(meterOfferPeriodMapping).flatMap((m) => m.displayOfferPeriodIds)
    );
    // Collect all recommended period IDs (each meter can select differently; needed for isBest and filtering)
    const recommendedPeriodIds = uniq(
      mergedMeterIds
        .map((mid) => meterOfferPeriodMapping[mid]?.recommendedPeriodId)
        .filter((id): id is string => Boolean(id))
    );

    // 3. Recalculate offers: each offer's meterIds = only meters that SELECTED this offer's period (recommendedPeriodId)
    // This avoids double-counting: only include offers matching each meter's recommended period, not all offers in offerIds
    if (mergedMeters.length === 0 || mergedOfferIds.length === 0 || mergedDisplayOfferPeriodIds.length === 0) {
      const firstGroup = groups[0];
      return {
        ...firstGroup,
        name: setName,
        meters: mergedMeters,
        offers: [],
        currentContestableCost: 0,
        currentAvgCostPerKwh: 0,
        meterOfferPeriodMapping
      } as EAReportGroupedMeter;
    }

    const formattedMeters = formatMeters(mergedMeters);

    const filteredOffersForComparison = offers.filter((offer) => offer.id && mergedOfferIds.includes(offer.id));
    const uniqueOffersMap = new Map<string, ProjectOffer>();
    filteredOffersForComparison.forEach((offer) => {
      if (offer.id && !uniqueOffersMap.has(offer.id)) {
        uniqueOffersMap.set(offer.id, offer);
      }
    });
    const deduplicatedOffers = Array.from(uniqueOffersMap.values());

    // Each offer is calculated only for the meters it actually covers (meterIdsForOffer).
    // aggregateForDisplay then shows (retailer, term) only when that offer has cost for EVERY meter in the merged group,
    // so retailer terms that don't cover all meters (e.g. Momentum for 4 of 5 meters) are correctly hidden.
    const projectRawOffers = deduplicatedOffers
      .map((offer) => {
        const meterIdsForOffer = mergedMeterIds.filter((mid) =>
          meterOfferPeriodMapping[mid]?.offerIds.includes(offer.id!)
        );
        const displayPeriodIdsForOffer = uniq(
          meterIdsForOffer.flatMap((mid) => meterOfferPeriodMapping[mid]?.displayOfferPeriodIds || [])
        );
        const contractRatePeriods = offer.contractRatePeriods || [];
        if (contractRatePeriods.length === 0 || meterIdsForOffer.length === 0) return null;
        const hasDisplayPeriod = contractRatePeriods.some(
          (p: { id?: string }) => p.id && displayPeriodIdsForOffer.includes(p.id)
        );
        if (!hasDisplayPeriod) return null;
        return {
          ...offer,
          meterIds: meterIdsForOffer,
          isBest: false,
          contractRatePeriods,
          environmentalRatePeriods: offer.environmentalRatePeriods || []
        };
      })
      .filter(Boolean) as any[];

    const calculatedOffers = calculateOffersForMeters({
      ...pick(formattedMeters, ['currentAvgCostPerKwh', 'lwaDLF', 'lwaMLF', 'annualData', 'weighedAverageData']),
      projectMeters: formattedMeters.meters as any[],
      projectRawOffers,
      greenPriceLookUps: greenPriceLookUps || []
    });

    // Build per-meter period from calculatedOffers (before merge) so each meter has its own rate/segments
    const meterPeriods: Record<string, import('./reportTypes').CalculatedProjectOfferPeriodsByName> = {};
    const selectedOffersList: { periodName: number; retailerName: string; meterId: string }[] = [];
    mergedMeterIds.forEach((meterId) => {
      const recId = meterOfferPeriodMapping[meterId]?.recommendedPeriodId;
      if (!recId) return;
      for (const offer of calculatedOffers) {
        const period = (offer.periods || []).find((p: { id?: string }) => p.id === recId);
        if (
          period &&
          (period.meterContestableCosts?.[meterId] != null ||
            (offer as { meterIds?: string[] }).meterIds?.includes(meterId))
        ) {
          meterPeriods[meterId] = period as import('./reportTypes').CalculatedProjectOfferPeriodsByName;
          const retailerName = (offer as { retailer?: { name?: string } }).retailer?.name ?? '?';
          selectedOffersList.push({ periodName: period.periodName, retailerName, meterId });
          return;
        }
      }
    });
    // Group by (retailerName, periodName) and collect meterIds so UI can show which meters per row
    const uniqueSelected = (() => {
      const key = (x: { retailerName: string; periodName: number }) => `${x.retailerName} (${x.periodName}M)`;
      const grouped = groupBy(selectedOffersList, key);
      return Object.entries(grouped).map(([_, items]) => ({
        retailerName: items[0].retailerName,
        periodName: items[0].periodName,
        meterIds: items.map((i) => i.meterId)
      }));
    })();
    // Show comparison block only when all selected offers have the same term (by periodName); hide when terms differ
    const selectedTerms = uniq(map(uniqueSelected, 'periodName'));
    const showComparison = uniqueSelected.length === 0 || selectedTerms.length === 1;

    const displayOffers = filterOffersByDisplayPeriodIds(
      aggregateForDisplay(
        calculatedOffers,
        recommendedPeriodIds,
        formattedMeters.meters as any[],
        meterOfferPeriodMapping,
        { useFullGroupCostsForPeriods: true }
      ),
      mergedDisplayOfferPeriodIds
    );

    const firstGroup = groups[0];

    return {
      ...firstGroup,
      name: setName,
      meters: formattedMeters.meters as any,
      offers: displayOffers as any,
      currentContestableCost: formattedMeters.currentContestableCost || 0,
      currentAvgCostPerKwh: formattedMeters.currentAvgCostPerKwh || 0,
      currentRegulatedCost: formattedMeters.currentRegulatedCost ?? firstGroup.currentRegulatedCost,
      currentRenewableCost: formattedMeters.currentRenewableCost ?? firstGroup.currentRenewableCost,
      meterOfferPeriodMapping,
      meterPeriods,
      // Keep recommendedPeriodId for backward compat (e.g. first meter's); per-meter data is in meterOfferPeriodMapping
      recommendedPeriodId: recommendedPeriodIds[0] || '',
      showComparison,
      selectedTermEntries: uniqueSelected,
      // annualData, weighedAverageData from formattedMeters so totals sum ALL merged meters
      annualData: formattedMeters.annualData,
      weighedAverageData: formattedMeters.weighedAverageData
    } as EAReportGroupedMeter;
  });

  return (
    <>
      {!isPrintVersion && !pathname.includes('share/') && (
        <>
          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 16, right: 16, bgcolor: 'brand.main' }}
            onClick={() => setMailComposerOpened(true)}
          >
            <EmailIcon />
          </Fab>
          <MailComposer
            open={mailComposerOpened}
            defaultEmail={company.contacts?.[0]?.email}
            defaultSubject={`[#${projectId}] E&A Report for ${company.tradingName}`}
            onClose={() => setMailComposerOpened(false)}
            contacts={
              map(companyContacts, (contact) => pick(contact, ['name', 'email'])) as { name: string; email: string }[]
            }
            templates={[
              {
                name: 'E&A',
                body: getEATemplate({
                  contactName: company.contacts?.[0]?.name,
                  companyName: company.tradingName,
                  groupedMeters
                })
              }
            ]}
          />
        </>
      )}

      {isPrintVersion && (
        <ReactToPrint
          trigger={() => (
            <Fab
              color="primary"
              aria-label="Print"
              sx={{ position: 'fixed', bottom: 16, right: 16, bgcolor: 'brand.main' }}
            >
              <PrintIcon />
            </Fab>
          )}
          content={() => componentRef.current}
          pageStyle="@page { size: A4 landscape;  margin: 10mm 0 10mm 0; }"
        />
      )}

      <ReportContainer ref={componentRef}>
        <AppBar component="nav" sx={{ bgcolor: 'common.black' }}>
          <Toolbar>
            <SectionContainer sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box component="img" src={logoBlueSrc} sx={{ height: 100 }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
                  <Typography
                    component="a"
                    variant="h6"
                    color="brand.main"
                    href={`tel:1300118834`}
                    sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
                  >
                    <CallIcon /> &nbsp;1300 11 88 34
                  </Typography>
                  <Typography
                    component="a"
                    variant="h6"
                    color="brand.main"
                    href={`mailto:1300118834`}
                    sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
                  >
                    <EmailIcon /> &nbsp;info@tteg.com.au
                  </Typography>
                  <Typography
                    component="a"
                    variant="h6"
                    color="brand.main"
                    href="https://tteg.com.au"
                    target="_blank"
                    sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
                  >
                    <LanguageIcon /> &nbsp;tteg.com.au
                  </Typography>
                </Box>
              </Box>
            </SectionContainer>
          </Toolbar>
        </AppBar>

        <Toolbar />

        <Section
          sx={{
            backgroundImage: `url(${reportCover})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundAttachment: 'fixed',
            height: '100%',
            p: 0
          }}
        >
          <SectionContainer sx={{ pt: 15, position: 'relative', height: 'calc(100vh - 64px)', minHeight: 500 }}>
            <Title variant="h1" color="common.black">
              ELECTRICITY
              <br />
              EVALUATION
              <br />& ANALYSIS
            </Title>
            <Box sx={{ position: 'absolute', right: 32, bottom: 32 }}>
              <Box sx={{ bgcolor: 'common.black', p: 2, mb: 2, color: 'brand.main' }}>
                <Typography variant="h6">Customer name: {companyName || company.tradingName}</Typography>
                <Typography variant="h6">ABN/ACN: {[company.abn, company.acn].filter((v) => !!v).join('/')}</Typography>
              </Box>

              <Box sx={{ bgcolor: 'common.white', p: 2, color: 'brand.main' }}>
                <Typography variant="h6">Prepared by:</Typography>
                <Typography variant="h6">{User.from(contactUser).fullName}</Typography>
              </Box>
            </Box>
          </SectionContainer>
        </Section>

        <SummarySection groupedMeters={groupedMeters} />

        {groupedMeters.map((groupedMeter, index) => (
          <Box key={`group-meter-box-${index}`}>
            {!isPrintVersion && (
              <BestOfferSection
                name={groupedMeter.name}
                meters={groupedMeter.meters}
                offers={groupedMeter.offers}
                showComparison={groupedMeter.showComparison}
                selectedTermEntries={groupedMeter.selectedTermEntries}
                showSegmentHelp={showSegmentHelp}
              />
            )}

            <ContractTermComparisonSection
              name={groupedMeter.name}
              meters={groupedMeter.meters}
              offers={groupedMeter.offers}
              currentContestableCost={groupedMeter.currentContestableCost}
              showSegmentHelp={showSegmentHelp}
            />

            {!isPrintVersion && (
              <CurrentVsNewBillSection name={groupedMeter.name} groupedMeter={groupedMeter} reportType={reportType} />
            )}
          </Box>
        ))}

        <SitesSection groupedMeters={groupedMeters} />
      </ReportContainer>
    </>
  );
};

export default ProjectEAReportPreview;
